import type React from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Close, WarningAmber } from "@mui/icons-material";
import { useConfirmPromise } from "@app/global/hooks/useConfirmPromise";
import { useForm } from "react-hook-form";
import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
    type ConfirmationFormSchema,
    createConfirmationSchema,
} from "./confirmationProvider.schema";
import { yupResolver } from "@hookform/resolvers/yup";

export enum ConfirmationResolution {
    Confirmed,
    Rejected,
}

export interface Confirmation {
    title: ReactNode;
    message?: ReactNode;
    requiredText?: string;
}

interface ConfirmationContextValue {
    confirm: (
        confirmation: Confirmation,
    ) => Promise<ConfirmationResolution | undefined>;
}

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null);

export const useConfirmation = () => {
    const context = useContext(ConfirmationContext);
    if (!context)
        throw new Error(
            "useConfirmation can only be used inside a ConfirmationProvider",
        );
    return context;
};

export const ConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({
                                                                                  children,
                                                                              }) => {
    const { t } = useTranslation(["common", "user"] as const);

    const [isOpen, setIsOpen] = useState(false);
    const [confirmation, setConfirmation] = useState<Confirmation>();

    const { promise, resolve, reject } =
        useConfirmPromise<ConfirmationResolution>();

    const requiredText = confirmation?.requiredText;

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isValid },
    } = useForm<ConfirmationFormSchema>({
        resolver: yupResolver(createConfirmationSchema(requiredText)),
        mode: "onChange",
    });

    const onSubmit = useCallback(() => {
        resolve(ConfirmationResolution.Confirmed);
        setIsOpen(false);
    }, [resolve]);

    const confirm = useCallback(
        async (conf: Confirmation) => {
            setConfirmation(conf);
            setIsOpen(true);

            reset({ confirmation: "" });

            try {
                return await promise();
            } catch {
                reject();
            } finally {
                setIsOpen(false);
            }
        },
        [promise, reject, reset],
    );

    const value = useMemo(() => ({ confirm }), [confirm]);

    return (
        <ConfirmationContext.Provider value={value}>
            <Dialog
                open={isOpen}
                onClose={() => resolve(ConfirmationResolution.Rejected)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
                maxWidth="xs"
            >
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <DialogTitle id="alert-dialog-title" sx={{ pr: 6 }}>
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                            {/* Default: warning icon always shown for this provider */}
                            <WarningAmber color="error" fontSize="small" />

                            <Typography variant="h6" >
                                {typeof confirmation?.title === "string"
                                    ? confirmation.title
                                    : confirmation?.title}
                            </Typography>
                        </Stack>

                        <IconButton
                            aria-label="close"
                            onClick={() => resolve(ConfirmationResolution.Rejected)}
                            sx={{
                                position: 'absolute',
                                right: 24,
                                top: 18,
                            }}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </DialogTitle>

                    {confirmation?.message && (
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                {confirmation.message}
                            </DialogContentText>
                        </DialogContent>
                    )}

                    {!!requiredText && (
                        <DialogContent sx={{ pt: confirmation?.message ? 0 : undefined }}>
                            <Typography
                                variant="caption"
                            >
                                {t("common:confirmations.type_prefix")}{" "}
                                <Typography variant="body1" component="span" >
                                    {requiredText}
                                </Typography>{" "}
                                {t("common:confirmations.type_suffix")}
                            </Typography>

                            <TextField
                                autoComplete="off"
                                autoFocus
                                id="confirmation-input"
                                {...register("confirmation")}
                                error={!!errors.confirmation}
                                fullWidth
                                size="small"
                            />
                        </DialogContent>
                    )}

                    <DialogActions >
                        <Button
                            variant="text"
                            color="secondary"
                            onClick={() => resolve(ConfirmationResolution.Rejected)}
                        >
                            {t("common:actions.cancel")}
                        </Button>

                        <Button
                            disabled={!!requiredText && !isValid}
                            type="submit"
                            color="error"
                            variant="contained"
                            data-test="confirmation-provider-confirm-button"
                        >
                            {t("common:actions.confirm")}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {children}
        </ConfirmationContext.Provider>
    );
};
